import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
} from '@mui/material';
import {
  CalendarToday,
  PictureAsPdf,
  Description,
  Visibility,
  DeleteOutline,
  Refresh,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import MizanService from '../../services/mizanService';
import { useCompany } from '../../contexts/CompanyContext';


const formatUploadedMizanFileName = (mizan) => {
  return String(mizan?.fileName || mizan?.generatedFileName || '-');
};

const UploadedTrialBalanceFilesList = ({
  refreshToken = 0,
  onViewMizan,
  onDeletedMizanId,
  onListLoaded,
}) => {
  const { companyId, taxId } = useCompany();
  const [uploadedMizans, setUploadedMizans] = useState([]);
  const [loadingMizans, setLoadingMizans] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchUploadedMizans = async () => {
    if (!taxId) return;
    setLoadingMizans(true);
    try {
      const result = await MizanService.getPeriodMizansByIdentifier(
        taxId,
        'TRIAL_BALANCE_UPLOAD'
      );
      if (result.success) {
        const list = result.data || [];
        list.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
        setUploadedMizans(list);
        onListLoaded?.(list);
      } else {
        toast.error(result.error || 'Failed to load trial balance documents.');
      }
    } catch (err) {
      console.error('Fetch uploaded trial balances error:', err);
      toast.error('Failed to load trial balance documents.');
    } finally {
      setLoadingMizans(false);
    }
  };

  useEffect(() => {
    fetchUploadedMizans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxId, refreshToken]);

  const handleDeleteMizan = async (mizanId) => {
    if (!window.confirm('Bu mizanı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }

    setDeletingId(mizanId);
    try {
      const isPeriodMizanId = typeof mizanId === 'number' || /^\d+$/.test(String(mizanId || ''));
      const result = isPeriodMizanId
        ? await MizanService.deletePeriodMizan(mizanId)
        : await MizanService.deleteMizan(mizanId);

      if (result.success) {
        toast.success('Mizan başarıyla silindi.');
        await fetchUploadedMizans();
        onDeletedMizanId?.(mizanId);
      } else {
        toast.error(result.error || 'Mizan silinirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Delete trial balance error:', err);
      toast.error('Mizan silinirken bir hata oluştu.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!companyId) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Uploaded Trial Balance Files
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={fetchUploadedMizans}
          disabled={loadingMizans}
          size="small"
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {loadingMizans ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {uploadedMizans.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No uploaded trial balance files found.
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                  File Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                  Date Range
                </TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                  Timestamp
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
              <TableBody>
                {uploadedMizans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  uploadedMizans.map((mizan, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'rgba(0,0,0,0.02)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {mizan.extension === 'pdf' ? (
                            <PictureAsPdf sx={{ color: '#d32f2f', fontSize: 20 }} />
                          ) : (
                            <Description sx={{ color: 'primary.main', fontSize: 20 }} />
                          )}
                          <Typography variant="body2">{formatUploadedMizanFileName(mizan)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{mizan.tarihAraligi || '-'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {mizan.timestamp || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              onViewMizan?.({
                                mizanId: mizan.mizanId,
                                fileName: formatUploadedMizanFileName(mizan),
                                donem: mizan.donem ?? null,
                                tarihAraligi: mizan.tarihAraligi ?? null,
                                periodCoveredStart: mizan.periodCoveredStart ?? mizan.periodStart ?? null,
                                periodCoveredEnd: mizan.periodCoveredEnd ?? mizan.periodEnd ?? null,
                              })
                            }
                            title="View"
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteMizan(mizan.mizanId)}
                            title="Delete"
                            disabled={deletingId === mizan.mizanId}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default UploadedTrialBalanceFilesList;

